import { FC } from 'react';

import { GiftDeliveryFeePreview } from '@shared/api/giftOrder/giftOrder.interface';
import { formatPrice } from '@shared/utils/formatPrice';

import { FreeShippingSuggestion } from '../utils/giftFreeShippingNudge.util';

interface IProps {
  preview: Extract<GiftDeliveryFeePreview, { deliverable: true }>;
  /** 어느 상품을 몇 개 더 담으면 되는지. 없으면 남은 금액·진행바만 */
  suggestion?: FreeShippingSuggestion | null;
  /** "N개 더 담기" — 수량을 올리면 미리보기가 다시 돌아 무료 적용이 확정된다 */
  onAdd?: (suggestion: FreeShippingSuggestion) => void;
  /** 미리보기 재확인 중 — 버튼 연타 방지 */
  busy?: boolean;
}

/**
 * 배송지가 정해진 뒤의 "조금만 더 담으면 기본배송비 무료" 안내.
 * 기본배송비가 붙어 있고 무료 기준이 있을 때만 렌더 — 남은 금액 + 진행바 + 한 번에 담기 버튼.
 */
export const GiftFreeShippingNudge: FC<IProps> = ({
  preview,
  suggestion,
  onAdd,
  busy = false,
}) => {
  const { rule, subtotal, baseFee, extraFee } = preview;
  if (!rule || baseFee <= 0 || rule.freeOverAmount <= subtotal) return null;

  const remaining = rule.freeOverAmount - subtotal;
  const percent = Math.min(Math.round((subtotal / rule.freeOverAmount) * 100), 100);

  return (
    <div className="mt-2 rounded-lg border bg-white p-2.5 text-xs text-gray-700 theme-boder-main">
      <p className="break-keep font-bold">
        <span className="theme-text-main">{formatPrice(remaining)}원</span>만 더 담으면
        기본배송비 {formatPrice(baseFee)}원이 무료
      </p>

      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full transition-all theme-bg-main"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="mt-1 text-[11px] text-gray-400">
        현재 {formatPrice(subtotal)}원 / 무료 기준 {formatPrice(rule.freeOverAmount)}원
      </p>

      {suggestion && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="min-w-0 break-keep text-gray-600">
            <span className="font-bold">{suggestion.itemName}</span>{' '}
            <span className="font-bold theme-text-main">{suggestion.addQty}개</span>만 더
            담으면 돼요
            <span className="text-gray-400">
              {' '}
              (+{formatPrice(suggestion.addCost)}원)
            </span>
          </p>
          {onAdd && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAdd(suggestion)}
              className="flex-shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 font-bold text-white theme-bg-main disabled:opacity-50"
            >
              {suggestion.addQty}개 더 담기
            </button>
          )}
        </div>
      )}

      {extraFee > 0 && (
        <p className="mt-1.5 break-keep text-[11px] text-gray-400">
          지역 추가배송비 {formatPrice(extraFee)}원은 무료 기준과 관계없이 유지됩니다.
        </p>
      )}
    </div>
  );
};
